// Simple script to read gift configuration
/* eslint-disable eslint-comments/no-unlimited-disable */

/* eslint-disable */

class FreeGiftManager {
  static #instance = null;
  static LINE_GIFT_PROPERTY = '_DiscountGenieGift';

  constructor() {
    if (FreeGiftManager.#instance) {
      throw new Error('Use FreeGiftManager.getInstance() to access the singleton instance.');
    }
  }

  static getInstance() {
    if (!FreeGiftManager.#instance) {
      FreeGiftManager.#instance = new FreeGiftManager();
    }
    return FreeGiftManager.#instance;
  }

  async getCartValue() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      const lineItems = cart.items.filter(item => !item.properties[FreeGiftManager.LINE_GIFT_PROPERTY]);
      const cartValue = lineItems.reduce((total, item) => total + Number(item.line_price), 0);
      return { cartValue, currency: cart.currency };
    } catch (error) {
      console.error('Error getting cart value:', error);
      return { cartValue: 0, currency: 'USD' };
    }
  }

  async getAvailableGifts() {
    const configElement = document.querySelector('[data-gwp-configuration]');
    if (!configElement) {
      console.log('No gift configuration found');
      return [];
    }

    let config, giftTiers, cartValue;
    try {
      config = JSON.parse(configElement.textContent);
      giftTiers = config['gwp-discount-config']?.gift_configuration?.gift_tiers || [];
      ({ cartValue } = await this.getCartValue());
    } catch (error) {
      console.error('Error parsing gift configuration or getting cart value:', error);
      return [];
    }

    const availableGifts = [];

    for (const tier of giftTiers) {
      const tierGifts = [];

      for (const product of tier.products) {
        try {
          const response = await fetch(`/products/${product.handle}.js`);
          if (!response.ok) {
            console.log(`Error fetching product ${product.handle}:`, response.status);
            continue;
          }

          const productData = await response.json();

          const isAvailable = cartValue >= tier.cart_goal * 100;

          tierGifts.push({
            handle: product.handle,
            title: productData.title,
            price: productData.price,
            available: productData.available,
            canBeAddedAsGift: isAvailable,
            featuredImage: productData.featured_image,
            variants: productData.variants.map(variant => ({
              id: variant.id,
              title: variant.title,
              price: variant.price,
              available: variant.available,
            })),
          });
        } catch (error) {
          console.log(`Error fetching product ${product.handle}:`, error);
        }
      }

      availableGifts.push({
        id: tier.id,
        name: tier.name,
        cartGoal: tier.cart_goal,
        currentCartValue: cartValue,
        products: tierGifts,
      });
    }

    return availableGifts;
  }

  async injectGiftUI(element) {
    if (!element) {
      console.error('No element provided to inject gift UI');
      return;
    }

    // Use singleton property for accordion state
    if (typeof this.accordionOpen === 'undefined') {
      this.accordionOpen = false; // default closed
    }
    const wasOpen = this.accordionOpen;

    try {
      // Fetch current cart contents
      const cart = await fetch('/cart.js').then(res => res.json());
      const cartVariantIds = new Set(cart.items.map(item => item.id));

      const availableGifts = await this.getAvailableGifts();

      if (availableGifts.length === 0) {
        element.innerHTML = '';
        return;
      }

      const html = `
        <details class="mb-4 rounded-lg bg-white group border border-gray-200 hidden"${wasOpen ? ' open' : ''} id="free-gift-accordion" >
          <summary class="cursor-pointer px-4 py-3 font-semibold text-base select-none rounded-t-lg flex items-center justify-between">
            <span>Jetzt Gratis-Produkt auswählen</span>
            <span class="ml-2 transition-transform duration-200 ease-in-out group-open:rotate-180" style="display: inline-flex;">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" class="bi bi-caret-down-fill" viewBox="0 0 16 16">
                <path d="M7.247 11.14 2.451 5.658C1.885 5.013 2.345 4 3.204 4h9.592a1 1 0 0 1 .753 1.659l-4.796 5.48a1 1 0 0 1-1.506 0z"/>
              </svg>
            </span>
          </summary>
          <div class="px-4 pb-4 pt-2 overflow-x-auto">
            <div class="flex gap-4">
              ${availableGifts
                .flatMap(tier =>
                  tier.products
                    .filter(gift => gift.available)
                    .map(gift => {
                      const variant = gift.variants[0];
                      const formId = `gift-form-${variant.id}`;
                      const giftProperty = JSON.stringify({ id: tier.id });
                      const isInCart = cartVariantIds.has(variant.id);
                      const canBeAddedAsGift = gift.canBeAddedAsGift;
                      const cardClasses = [
                        'border',
                        isInCart ? 'border-blue-600' : 'border-gray-200',
                        'rounded-lg',
                        'shadow-sm',
                        'p-2',
                        'flex',
                        'flex-col',
                        'justify-between',
                        'items-center',
                        'bg-white',
                        'h-full',
                        canBeAddedAsGift ? '' : 'opacity-50 pointer-events-none',
                      ].join(' ');
                      return `
                        <div class="basis-[150px] flex-shrink-0 flex-grow-0 flex flex-col relative">
                          <div class="text-sm text-gray-500 mb-1 text-center">
                            Ab ${new Intl.NumberFormat(`${Shopify?.locale}-${Shopify?.country}`, { style: 'currency', currency: Shopify?.currency?.active }).format(tier.cartGoal)}
                          </div>
                          <div class="${cardClasses}">
                            <div class="w-full aspect-square mb-1 relative">
                              <img src="${gift.featuredImage}" alt="${gift.title}" class="w-full h-full object-contain absolute top-0 left-0 rounded-md" />
                            </div>
                            <div class="flex-1 w-full flex flex-col">
                              <div class="type-body-s text-center leading-[1.2] mb-1">${gift.title}</div>
                              <div class="font-bold text-center mb-2">wert ${new Intl.NumberFormat(`${Shopify?.locale}-${Shopify?.country}`, { style: 'currency', currency: Shopify?.currency?.active }).format(gift.price / 100)}</div>
                            </div>
                            <form is="product-form" id="${formId}" class="w-full mt-auto">
                              <input type="hidden" name="id" value="${variant.id}">
                              <input type="hidden" name="quantity" value="1">
                              <input type="hidden" name="properties[${FreeGiftManager.LINE_GIFT_PROPERTY}]" value='${giftProperty}'>
                              <button 
                                type="submit"
                                class="gift-add-button text-center w-full mt-1 ${isInCart ? 'bg-gray-300 text-gray-700 border border-gray-400' : 'bg-blue-600 text-white hover:bg-blue-700'} text-sm font-medium py-1.5 px-2 rounded-md transition before:content-[''] before:absolute before:w-full before:h-full before:top-0 before:left-0"
                                ${isInCart ? 'disabled' : ''}
                                title="${isInCart ? 'Ausgewählt' : ''}"
                                data-loading-text="Lädt..."
                                data-original-text="${isInCart ? 'Ausgewählt' : 'Hinzufügen'}"
                              >
                                ${isInCart ? 'Ausgewählt' : 'Hinzufügen'}
                              </button>
                            </form>
                          </div>
                        </div>
                      `;
                    }),
                )
                .join('')}
            </div>
          </div>
        </details>
      `;
      element.innerHTML = html;

      // Add event listener to persist accordion state in singleton
      const details = element.querySelector('details');
      if (details) {
        details.addEventListener('toggle', () => {
          this.accordionOpen = details.open;
        });
      }
    } catch (error) {
      console.error('Error injecting gift UI:', error);
      element.innerHTML = '<p class="error">Unable to load gift options</p>';
    }
  }

  setupGiftEventListeners(container) {
    container.addEventListener(
      'submit',
      async event => {
        if (!event.target.hasAttribute('is') || event.target.getAttribute('is') !== 'product-form') {
          return;
        }
        if (event.__freeGiftHandled) return;

        event.preventDefault();
        event.stopImmediatePropagation();

        const button = event.target.querySelector('button[type="submit"]');
        let originalText;
        if (button) {
          button.disabled = true;
          originalText = button.textContent;
          button.setAttribute('data-original-text', originalText);
          button.textContent = button.getAttribute('data-loading-text') || 'Lädt...';
        }

        try {
          // Remove the first free gift in the cart (if any)
          const cart = await fetch('/cart.js').then(res => res.json());
          const freeGiftItem = cart.items.find(
            item => item.properties && item.properties[FreeGiftManager.LINE_GIFT_PROPERTY],
          );
          if (freeGiftItem) {
            await fetch('/cart/change.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ id: freeGiftItem.key, quantity: 0 }),
            });
          }

          // Re-dispatch the submit event to allow the Shopify handler to run
          const newEvent = new Event('submit', { bubbles: true });
          newEvent.__freeGiftHandled = true;
          event.target.dispatchEvent(newEvent);
        } catch (error) {
          console.error('Error injecting gift UI:', error);
          if (button) {
            button.disabled = false;
            button.textContent = button.getAttribute('data-original-text') || 'Hinzufügen';
          }
        }
      },
      { capture: true },
    );
  }
}

// Only expose the singleton instance
window.FreeGiftManager = FreeGiftManager;
